import React from 'react';
import { Box, Typography, LinearProgress, Chip, Stack } from '@mui/material';
import { TrendingUp, TrendingDown, AttachMoney, Inventory } from '@mui/icons-material';
import PremiumCard from '../../ui/PremiumCard';

/**
 * WIDGET ÚNICO: Análisis de Rentabilidad por Producto
 * Esto NO existe en Tiendanube - es valor agregado puro
 */
export default function ProfitabilityAnalysisCard({ productsData = [] }) {
  // Calcular métricas de rentabilidad
  const profitabilityAnalysis = React.useMemo(() => {
    if (!productsData || productsData.length === 0) return null;

    const analysis = productsData.map(product => {
      const revenue = product.revenuePaid || product.totalRevenue || 0;
      const units = product.unitsSoldPaid || product.totalQuantity || 1;
      const avgPrice = revenue / units;
      
      // Estimación de costo (70% del precio promedio - ajustable)
      const estimatedCost = avgPrice * 0.7;
      const profit = avgPrice - estimatedCost;
      const profitMargin = (profit / avgPrice) * 100;
      
      return {
        name: product.name || 'Producto sin nombre',
        revenue,
        units,
        avgPrice,
        profit,
        profitMargin,
        profitTotal: profit * units
      };
    }).sort((a, b) => b.profitTotal - a.profitTotal);

    const totalProfit = analysis.reduce((sum, item) => sum + item.profitTotal, 0);
    const avgMargin = analysis.reduce((sum, item) => sum + item.profitMargin, 0) / analysis.length;

    return {
      products: analysis.slice(0, 5), // Top 5
      totalProfit,
      avgMargin
    };
  }, [productsData]);

  if (!profitabilityAnalysis) {
    return (
      <PremiumCard 
        title="Análisis de Rentabilidad" 
        subtitle="Valor agregado exclusivo de Nubelytics"
        icon={<AttachMoney />}
        color="success"
        exclusive
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No hay datos suficientes para el análisis de rentabilidad
        </Typography>
      </PremiumCard>
    );
  }

  return (
    <PremiumCard 
      title="Análisis de Rentabilidad por Producto"
      subtitle="Insights que Tiendanube no te muestra"
      icon={<AttachMoney />}
      color="success"
      exclusive
    >
        {/* Resumen general */}
        <Box mb={3}>
          <Stack direction="row" spacing={2} mb={2}>
            <Box>
              <Typography variant="h6" color="success.main">
                ${profitabilityAnalysis.totalProfit.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ganancia Total Estimada
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="primary.main">
                {profitabilityAnalysis.avgMargin.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Margen Promedio
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Top productos por rentabilidad */}
        <Typography variant="subtitle2" gutterBottom>
          Top Productos por Rentabilidad
        </Typography>
        <Stack spacing={2}>
          {profitabilityAnalysis.products.map((product, index) => (
            <Box key={index}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" fontWeight={500}>
                  {product.name.length > 30 ? product.name.substring(0, 30) + '...' : product.name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {product.profitMargin > 25 ? (
                    <TrendingUp color="success" fontSize="small" />
                  ) : (
                    <TrendingDown color="warning" fontSize="small" />
                  )}
                  <Typography variant="body2" fontWeight={600}>
                    {product.profitMargin.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(product.profitMargin, 100)} 
                color={product.profitMargin > 25 ? "success" : "warning"}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {product.units} unidades vendidas
                </Typography>
                <Typography variant="caption" color="success.main">
                  +${product.profitTotal.toLocaleString()} ganancia
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        {/* Insights automáticos */}
        <Box mt={3} p={2} bgcolor="primary.50" borderRadius={2}>
          <Typography variant="subtitle2" color="primary.main" gutterBottom>
            💡 Insight Automático
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {profitabilityAnalysis.avgMargin > 30 
              ? "Excelente rentabilidad general. Considera expandir los productos más rentables."
              : profitabilityAnalysis.avgMargin > 20
              ? "Rentabilidad saludable. Revisa los productos con menor margen."
              : "Oportunidad de mejora en márgenes. Analiza costos y precios."
            }
          </Typography>
        </Box>
    </PremiumCard>
  );
}
